import React, { useState } from 'react'
import { ColumnsType } from 'antd/es/table'
import { Table } from 'antd'
import { Unit } from '@/model/types/goods'
import $msg from '@/utils/message'
import { IPagination } from '@/model/types/common'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import StockSearchDetail from '../stock-search-detail/stock-search-detail'
import { showLayer } from '@/utils/layer'
import { SorterResult } from 'antd/lib/table/interface'
import { stockSearchModel } from '../model'
import { observer } from 'mobx-react'
import { stockSearchFilterStore } from '../stock-search-filter/filter-store'
import { staffStore } from '@/store/staff'

interface StockSearchTableProps {
    date: string[]
}

export interface StockList {
    book_cost: null | number
    closing_stock: number
    cost_price: null | number
    current_stock: number
    goods_id: number
    goods_name: string
    img_url: string
    opening_stock: number
    stock_unit: Unit
}

const StockSearchTable: React.FC<StockSearchTableProps> = observer(props => {
    const hasCostPermission = !!staffStore.canIUse(39)
    const [sortedInfo, setSortedInfo] = useState<SorterResult<StockList> | null>(null)

    const searchColumns: ColumnsType<StockList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods_name',
            dataIndex: 'goods_name',
        },
        {
            title: '库存单位',
            key: 'stock_unit',
            dataIndex: 'stock_unit',
            render: val => val.name,
        },
        {
            title: '期初库存',
            key: 'opening_stock',
            dataIndex: 'opening_stock',
        },
        {
            title: '期末库存',
            key: 'closing_stock',
            dataIndex: 'closing_stock',
        },
        {
            title: '操作',
            key: 'action',
            width: 90,
            align: 'center',
            render: (val, record) => (
                <span
                    className="action"
                    onClick={() => {
                        showLayer(<StockSearchDetail stockId={record.goods_id} />)
                    }}
                >
                    详情
                </span>
            ),
        },
    ]

    const columns: ColumnsType<StockList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods_name',
            dataIndex: 'goods_name',
        },
        {
            title: '库存单位',
            key: 'stock_unit',
            width: 80,
            dataIndex: 'stock_unit',
            render: val => val.name,
        },
        {
            title: '实时库存',
            key: 'current_stock',
            width: 120,
            dataIndex: 'current_stock',
            sorter: (a, b) => a.current_stock - b.current_stock,
            // @ts-ignore
            sortOrder: sortedInfo?.columnKey === 'current_stock' && sortedInfo?.order,
        },
        {
            title: '成本价',
            key: 'cost_price',
            dataIndex: 'cost_price',
            render: val => (val ? `¥${val}` : '-'),
        },
        {
            title: '账面成本',
            key: 'book_cost',
            dataIndex: 'book_cost',
            render: val => (val ? `¥${val}` : '-'),
        },
        {
            title: '操作',
            key: 'action',
            width: 90,
            align: 'center',
            render: (val, record) => (
                <span
                    className="action"
                    onClick={() => {
                        showLayer(<StockSearchDetail stockId={record.goods_id} />)
                    }}
                >
                    详情
                </span>
            ),
        },
    ]

    const requestHandle = useRequest<
        {
            list: StockList[]
            pagination: IPagination
        },
        {
            stock_infos: StockList[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/warehouse/getgoodsstockinfos',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    from_date: props.date[0],
                    to_date: props.date[1],
                    keyword: stockSearchModel.keyWord || void 0,
                    ...stockSearchFilterStore.filterQuery,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.stock_infos,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
            refreshDeps: [props.date, stockSearchModel.keyWord, stockSearchFilterStore.filterQuery],
        },
    )

    return (
        <div className="table-container">
            <AutoResizer>
                {({ height }) => {
                    return (
                        <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                            <Table
                                rowKey={record => record.goods_id}
                                loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                                bordered
                                pagination={false}
                                columns={
                                    stockSearchModel.keyWord || props.date[0]
                                        ? searchColumns
                                        : hasCostPermission
                                        ? columns
                                        : columns.filter(
                                              column => column.key !== 'cost_price' && column.key !== 'book_cost',
                                          )
                                }
                                dataSource={requestHandle.data?.list}
                                scroll={{ x: 'max-content', y: height - 50 }}
                                onChange={(page, filter, sorter) => {
                                    setSortedInfo(sorter as any)
                                }}
                            />
                        </WithScrollLoad>
                    )
                }}
            </AutoResizer>
        </div>
    )
})

export default StockSearchTable
